import { useEffect, useState } from 'react';
import { useQuery } from "@tanstack/react-query";
import useUserToken from '../../context/userTokenContext';
import useLocationsStore from '../../context/locationsStore';
import { getGatewaysCounters, getDevicesCounters, getLocationsData, getGateways } from '../../api/apiCalls';
import useNavbarSearchStore from '../../context/useNavBarSearchStore';
import BarChart from '../charts/bar';
import Stats from '../stats';

function Dashboard() {

  const { locations, setLocations } = useLocationsStore();

  const userToken = useUserToken((state) => state.userToken);
  const [gatewayCounters, setGatewayCounters] = useState([]);
  const [countersSuccess, setCountersSuccess] = useState(false);
  const [gatewaysLastUpdate, setGatewaysLastUpdate] = useState(null);

  const [devicesCounters, setDevicesCounters] = useState([]);
  const [devicesSuccess, setDevicesSuccess] = useState(false);
  const [devicesLastUpdate, setDevicesLastUpdate] = useState(null);

  // const [locations, setLocations] = useState(null);
  const [locationsSuccess, setLocationsSuccess] = useState(false);

  const { setSearchList } = useNavbarSearchStore();

  const { data: locationsData, isLoading: locationsLoading, isError: locationsIsError, error: locationsError } = useQuery({
    queryKey: ['getLocationsData'],
    queryFn: getLocationsData
  });

  const { data: gatewayCountersData, isLoading: gatewayCountersIsLoading, isError: gatewayCountersIsError, error: gatewayCountersError } = useQuery({
    queryKey: ['getGatewaysCounters'],
    queryFn: getGatewaysCounters
  });

  const { data: devicesData, isLoading: devicesIsLoading, isError: devicesIsError, error: devicesError } = useQuery({
    queryKey: ['getDevicesCounters'],
    queryFn: getDevicesCounters
  });

  const { data: gatewaysData, isLoading: gatewaysIsLoading, isError: gatewaysIsError, error: gatewaysError } = useQuery({
    queryKey: ['getGatewaysData'],
    queryFn: getGateways
  });

  useEffect(() => {
    if (gatewayCountersData && gatewayCountersData.data) {
      let totalCounters = { total: 0, active: 0, fail: 0, init: 0, error: 0 };
      Object.entries(gatewayCountersData.data.groups).forEach(([domainName, domainData]) => {
        if (domainName !== 'global') {
          totalCounters.total += domainData.total;
          totalCounters.active += domainData.active;
          totalCounters.fail += domainData.fail;
          totalCounters.init += domainData.init;
          totalCounters.error += domainData.error;
        }
      });
      setGatewayCounters([totalCounters]);
      setGatewaysLastUpdate(gatewayCountersData.data.lastUpdate);
      setCountersSuccess(true);
    }
  }, [gatewayCountersData]);

  useEffect(() => {
    if (devicesData && devicesData.data) {
      let totalCounters = { total: 0, active: 0, fail: 0, init: 0, error: 0 };
      Object.entries(devicesData.data.groups).forEach(([domainName, domainData]) => {
        if (domainName !== 'global') {
          totalCounters.total += domainData.total;
          totalCounters.active += domainData.active;
          totalCounters.fail += domainData.fail;
          totalCounters.init += domainData.init;
          totalCounters.error += domainData.error;
        }
      });
      setDevicesCounters([totalCounters]);
      setDevicesLastUpdate(devicesData.data.lastUpdate);
      setDevicesSuccess(true);
    }
  }, [devicesData]);

  useEffect(() => {
    if (locationsData && locationsData.data) {

      console.log('locationsData');
      console.log(locationsData.data.groupedLocations);
      setLocations(locationsData.data.groupedLocations);
      setLocationsSuccess(true);
    }
  }, [locationsData]);

  useEffect(() => {
    let searchListArr = [];

    if (gatewaysData && gatewaysData.data) {
      Object.entries(gatewaysData.data.domains).forEach(([domainName, domainData]) => {
        domainData.list.forEach((gateway) => {
          searchListArr.push(
            {
              name: gateway.name,
              id: gateway.lrrid,
              server: gateway.server
            }
          );
        });
      });

      setSearchList(searchListArr);
    }
  }, [gatewaysData]);

  return (
    <div className="flex flex-col mt-10">
      {gatewaysLastUpdate && gatewaysLastUpdate !== null ?
        <h1 className="text-[var(--textPrimary)] font-bold text-xl">Gateways
          <span className="badge badge-outline ml-4">{new Date(gatewaysLastUpdate).toLocaleString('en-GB')}</span>
        </h1>
        :
        <h1 className="text-[var(--textPrimary)] font-bold text-xl">Gateways
          <span className="badge badge-outline ml-4">
            <span className="animate-pulse bg-[var(--skeleton-bg)] rounded-full h-4 w-36"></span>
          </span>
        </h1>
      }

      {countersSuccess ?
        <div>
          <Stats counters={gatewayCounters} type="gateways" />
        </div>
        :
        <div>
          <Stats skeleton={true} />
        </div>
      }
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-4 mb-10">
        {gatewayCountersIsLoading &&
          Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="skeleton animate-pulse bg-[var(--skeleton-bg)] rounded-2xl mt-6 w-full h-72"
            ></div>
          ))}

        {gatewayCountersIsError && (
          <h1>Error: {gatewayCountersError.message}</h1>
        )}

        {/* {gatewayCountersData && locationsSuccess &&
          Object.entries(gatewayCountersData.data.groups).map(
            ([groupName, stats], index) => (
              <div
                key={index}
                className="w-full bg-[var(--primary)] rounded-2xl mt-6"
              >
                <BarChart
                  title={locations.length > 0 && locations[groupName].name ? locations[groupName].name : groupName}
                  labels={['Active', 'Fail', 'Error', 'Init']}
                  series={[stats.active, stats.fail, stats.error, stats.init]}
                />
              </div>
            )
          )} */}
        {gatewayCountersData && locationsSuccess &&
          Object.entries(gatewayCountersData.data.groups).map(([groupName, stats], index) => {
            // Get location name safely
            const locationName = locations?.[groupName]?.name || groupName;

            if (locationName === 'global') return null;

            return (
              <div
                key={index}
                className="w-full bg-[var(--primary)] rounded-2xl mt-6"
              >
                <BarChart
                  title={locationName}
                  labels={['Active', 'Fail', 'Error', 'Init']}
                  series={[stats.active, stats.fail, stats.error, stats.init]}
                />
              </div>
            );
          })}
      </div>

      {devicesLastUpdate && devicesLastUpdate !== null ?
        <h1 className="text-[var(--textPrimary)] font-bold text-xl">Devices
          <span className="badge badge-outline ml-4">{new Date(devicesLastUpdate).toLocaleString('en-GB')}</span>
        </h1>
        :
        <h1 className="text-[var(--textPrimary)] font-bold text-xl">Devices
          <span className="badge badge-outline ml-4">
            <span className="animate-pulse bg-[var(--skeleton-bg)] rounded-full h-4 w-36"></span>
          </span>
        </h1>
      }

      {devicesSuccess && devicesCounters.length > 0 ?
        <div>
          <Stats counters={devicesCounters} type="devices" />
        </div>
        :
        <div>
          <Stats skeleton={true} />
        </div>
      }
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6 gap-4 mb-8">
        {devicesIsLoading &&
          Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="animate-pulse bg-[var(--skeleton-bg)] rounded-2xl mt-6 w-full h-56"
            ></div>
          ))}

        {devicesIsError && <h1>Error: {devicesError.message}</h1>}

        {/* {devicesData &&
          Object.entries(devicesData.data.groups).map(
            ([groupName, stats], index) => (
              <div
                key={index}
                className="w-full bg-[var(--primary)] rounded-2xl mt-6"
              >
                <BarChart
                  title={groupName}
                  labels={['Active', 'Fail', 'Error', 'Init']}
                  series={[stats.active, stats.fail, stats.error, stats.init]}
                />
              </div>
            )
          )} */}
        {devicesData && locationsSuccess &&
          Object.entries(devicesData.data.groups).map(([groupName, stats], index) => {
            // Get location name safely
            const locationName = locations?.[groupName]?.name || groupName;

            if (locationName === 'global') return null;

            return (
              <div
                key={index}
                className="w-full bg-[var(--primary)] rounded-2xl mt-6"
              >
                <BarChart
                  title={locationName}
                  labels={['Active', 'Fail', 'Error', 'Init']}
                  series={[stats.active, stats.fail, stats.error, stats.init]}
                />
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default Dashboard;
